/**
 * Consent state + the consent-gated persistent visitor cookie (`cmg_vid`) —
 * the v3 "hybrid identity" half of the `/ig` bio-link pipeline (design
 * contract: `.claude/handoffs/tracking-v3-design.md`, decision 1). Cookieless
 * Tier-0 tracking (daily-rotating visitor hash, `./attribution.ts`) already
 * covers 100% of visitors regardless of consent; THIS module layers a
 * 365-day first-party `cmg_vid` cookie on top, ONLY for visitors who
 * explicitly accept the cookie banner — unlocking returning-visitor
 * recognition, first-touch attribution, and cohorts for that consenting
 * subset.
 *
 * Browser-only (cookies-next/client + navigator/window/document APIs) — no
 * 'use client' directive needed (this is a plain utility module, not a React
 * component, same pattern as ./attribution.ts and ./engagement.ts), so it's
 * safe to import from any client component without pulling server-only code
 * into the bundle.
 *
 * GPC (Global Privacy Control, https://globalprivacycontrol.org/) is
 * honored as an automatic decline — a GPC signal always wins over ANY
 * stored `cookie-accept` cookie, even a prior explicit "accept" (a visitor
 * who has since enabled GPC in their browser is exercising a fresh,
 * overriding choice). GPC visitors never get a `cmg_vid`, and any
 * pre-existing one is expired the next time `getVisitorId()` runs.
 *
 * Cookie names: `cookie-accept` (pre-existing — `src/components/Cookies.tsx`
 * used to read/write this directly with its own `setCookie`/`getCookie`
 * calls; this module is now the sole owner of that cookie, same name, but
 * see CONSENT_VERSION below for why the VALUES are no longer the bare
 * pre-existing ones) and `cmg_vid` (new, v3, this module only).
 *
 * CONSENT VERSIONING (bug fix, live-proven 2026-07-17: owners who only ever
 * saw the OLD banner showed "Consented visits: 0" despite real visits — they
 * were permanently grandfathered as answered and never re-asked). The old
 * banner was itself the non-compliant design this v3 redesign replaced (its
 * X button silently wrote 'declined' for 365 days — a forced-choice dark
 * pattern) — so every historical `cookie-accept` value was written under a
 * consent UX EDPB/CNIL guidance would not recognize as a valid choice under
 * the new banner's terms. Writing/reading versioned values (`accepted.v2` /
 * `declined.v2`, bumped via `CONSENT_VERSION`) means a banner redesign that
 * materially changes the consent context re-asks EVERY existing visitor
 * exactly ONCE, rather than treating a legacy value as an eternal answer —
 * this is the standard, compliant response to a consent-UX change, not a
 * regression. Any raw/legacy value (unversioned 'accepted'/'declined', or
 * anything else) is treated as 'unset' by `getConsentState()` below. Bump
 * `CONSENT_VERSION` again the next time a change is material enough to
 * warrant re-asking (e.g. a future copy change that alters what "accept"
 * means is NOT material; a new consent-UX/legal-basis change IS).
 *
 * Related: `.claude/handoffs/tracking-v3-design.md` · ./attribution.ts ·
 * ./engagement.ts · ../../components/Cookies.tsx
 */

import { getCookie, setCookie, deleteCookie } from 'cookies-next/client';

const CONSENT_COOKIE_KEY = 'cookie-accept';
const VISITOR_COOKIE_KEY = 'cmg_vid';
const CONSENT_COOKIE_DAYS = 365;
const VISITOR_COOKIE_DAYS = 365;

/**
 * Bump on any FUTURE material consent-context change (banner redesign,
 * legal-basis change, new consent-relevant copy) — see the module docstring
 * "CONSENT VERSIONING" section for why this exists. `recordConsent()` writes
 * `accepted.v${CONSENT_VERSION}` / `declined.v${CONSENT_VERSION}`;
 * `getConsentState()` accepts ONLY the current version's values and treats
 * every other value (older version, or anything unrecognized) as 'unset' —
 * bumping this constant is what makes a version bump re-ask everyone once.
 */
const CONSENT_VERSION = 2;

/** Dispatched on `window` by `recordConsent()` — see `onConsentChange()`. */
const CONSENT_CHANGE_EVENT = 'cmg-consent-change';

export type ConsentState = 'accepted' | 'declined' | 'unset' | 'gpc';

function cookieOptions(
  days: number,
  sameSite: 'strict' | 'lax'
): { expires: Date; sameSite: 'strict' | 'lax'; secure: boolean; path: string } {
  return {
    expires: new Date(Date.now() + days * 24 * 60 * 60 * 1000),
    sameSite,
    secure: process.env.NODE_ENV === 'production',
    path: '/',
  };
}

/**
 * True when the browser sends a Global Privacy Control signal
 * (`navigator.globalPrivacyControl === true`). Non-standard property, not
 * yet part of TS's DOM lib — this cast is the only place this repo reads it.
 */
function hasGPC(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  return nav.globalPrivacyControl === true;
}

/**
 * Current consent state. GPC is checked FIRST and always wins — a live GPC
 * signal overrides even a previously-recorded 'accepted' cookie (design
 * decision 2: "GPC honored as auto-decline"). Falls back to the
 * `cookie-accept` cookie, then 'unset' when neither applies (first visit, a
 * visitor who dismissed the banner via X without choosing — see the
 * sessionStorage `cmg_consent_snooze` handled by the banner component itself,
 * not here — OR a legacy/other-version value, see below).
 *
 * ONLY the current version's exact values (`accepted.v${CONSENT_VERSION}` /
 * `declined.v${CONSENT_VERSION}`) are recognized as a real answer — any other
 * string (an unversioned legacy 'accepted'/'declined' from the pre-v3 banner,
 * a stale older version, or junk) falls through to 'unset' so the banner
 * re-asks once. This is deliberate, not a parsing gap: see CONSENT_VERSION's
 * docstring and the module docstring's "CONSENT VERSIONING" section.
 */
export function getConsentState(): ConsentState {
  if (hasGPC()) return 'gpc';
  if (typeof document === 'undefined') return 'unset';

  const raw = getCookie(CONSENT_COOKIE_KEY);
  if (raw === `accepted.v${CONSENT_VERSION}`) return 'accepted';
  if (raw === `declined.v${CONSENT_VERSION}`) return 'declined';
  return 'unset';
}

/**
 * Record an explicit accept/decline choice: writes the `cookie-accept`
 * cookie (365d — same name/duration as the pre-v3 Cookies.tsx behavior, only
 * the write path moved here AND the value is now versioned —
 * `accepted.v${CONSENT_VERSION}` / `declined.v${CONSENT_VERSION}`, see
 * CONSENT_VERSION's docstring) and broadcasts `cmg-consent-change` on
 * `window` so any mounted listener (the banner itself, or a future consumer)
 * reacts immediately without a page reload. Declining also proactively
 * expires any existing `cmg_vid` — see `getVisitorId()`'s docstring for the
 * create-on-accept path and the lazy (GPC-covering) expiry it also performs.
 */
export function recordConsent(state: 'accepted' | 'declined'): void {
  if (typeof document === 'undefined') return;

  setCookie(
    CONSENT_COOKIE_KEY,
    `${state}.v${CONSENT_VERSION}`,
    cookieOptions(CONSENT_COOKIE_DAYS, 'strict')
  );

  if (state === 'declined') {
    deleteCookie(VISITOR_COOKIE_KEY, { path: '/' });
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent<ConsentState>(CONSENT_CHANGE_EVENT, { detail: state }));
  }
}

/**
 * Subscribe to consent changes broadcast by `recordConsent()` — same-tab
 * only (cross-tab sync would need a `storage` event listener; deliberately
 * out of scope, see the design contract's deferred list). Returns an
 * unsubscribe function, always safe to call even with no `window` (SSR).
 */
export function onConsentChange(cb: (state: ConsentState) => void): () => void {
  if (typeof window === 'undefined') return () => {};

  function handler(event: Event): void {
    cb((event as CustomEvent<ConsentState>).detail);
  }

  window.addEventListener(CONSENT_CHANGE_EVENT, handler);
  return () => window.removeEventListener(CONSENT_CHANGE_EVENT, handler);
}

/**
 * Read-or-create the persistent visitor cookie — ONLY when consent is
 * 'accepted'. Returns null for every other state ('declined' | 'unset' |
 * 'gpc'), and actively EXPIRES any stale `cmg_vid` left over from a prior
 * acceptance when the visitor has since declined or is now sending GPC (a
 * visitor's later "no" must not leave their old persistent id readable —
 * this is the lazy counterpart to the proactive expiry in `recordConsent`'s
 * decline branch, and the ONLY place the GPC case is ever cleaned up, since
 * GPC has no explicit "record" call of its own — it's live ambient browser
 * state checked fresh on every call).
 *
 * 'unset' deliberately does NOT expire the cookie here — there is nothing to
 * revoke yet (no explicit choice was ever made), so this stays a read-only
 * no-op rather than a destructive one.
 */
export function getVisitorId(): string | null {
  const state = getConsentState();

  if (state === 'accepted') {
    if (typeof document === 'undefined') return null;
    const existing = getCookie(VISITOR_COOKIE_KEY);
    if (typeof existing === 'string' && existing) return existing;

    const created = crypto.randomUUID();
    setCookie(VISITOR_COOKIE_KEY, created, cookieOptions(VISITOR_COOKIE_DAYS, 'lax'));
    return created;
  }

  if (state === 'declined' || state === 'gpc') {
    if (typeof document !== 'undefined') deleteCookie(VISITOR_COOKIE_KEY, { path: '/' });
  }

  return null;
}
